import { Injectable, signal } from '@angular/core';

/**
 * Which answers on this page came from the AI, which questions it looked at
 * and could not answer, and whether a compilation is running right now.
 *
 * Provenance is per *answer*, not per compile path: an answer produced by the
 * per-question button and one produced by the bulk run are equally AI-written,
 * so both record it here and the widget badge means what it says. Keeping it out
 * of `BulkCompileService` also lets the leaf widget button read it without
 * depending on the batch orchestrator.
 */
@Injectable()
export class AutoCompileStateService {
  /**
   * Widget id → the justification the model gave (`null` when it gave none).
   * Presence in the map is what marks an answer as AI-written.
   */
  readonly aiAnswers = signal<ReadonlyMap<string, string | null>>(new Map());

  /**
   * Widget id → why the batch proposed nothing for it.
   *
   * A question the batch looked at and could not answer keeps the model's
   * reason next to it, so the applicant knows the documents did not cover it
   * rather than that the AI skipped it. Kept apart from `aiAnswers` because it
   * says the opposite: nothing listed here was written by the AI, and the
   * badge must not claim it was.
   */
  readonly noteJustifications = signal<ReadonlyMap<string, string>>(new Map());

  /**
   * An automatic compilation is in flight — single-question or bulk. Every
   * compile trigger disables itself on this, so the two paths cannot overlap in
   * either direction.
   */
  readonly busy = signal(false);

  markAiCompiled(widgetId: string, justification: string | null): void {
    this.aiAnswers.update((current) => {
      const next = new Map(current);
      next.set(widgetId, justification);
      return next;
    });

    // An answer supersedes a note: once the AI has written something there is
    // no longer a "nothing" to explain.
    this.noteJustifications.update((current) => {
      if (!current.has(widgetId)) return current;
      const next = new Map(current);
      next.delete(widgetId);
      return next;
    });
  }

  markNoProposal(widgetId: string, justification: string): void {
    this.noteJustifications.update((current) => {
      const next = new Map(current);
      next.set(widgetId, justification);
      return next;
    });
  }

  isAiCompiled(widgetId: string | undefined): boolean {
    return !!widgetId && this.aiAnswers().has(widgetId);
  }

  justificationFor(widgetId: string | undefined): string | null {
    if (!widgetId) return null;
    return this.aiAnswers().get(widgetId) ?? null;
  }

  noteFor(widgetId: string | undefined): string | null {
    if (!widgetId) return null;
    return this.noteJustifications().get(widgetId) ?? null;
  }
}
